import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { formatCurrency, toBaseCurrency, formatDate, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { Car, Plus, Trash2, Fuel, Wrench, Route } from "lucide-react";

const LOG_TYPES = {
  fuel: { label: "Combustível", icon: Fuel, cls: "bg-amber-500/20 text-amber-600" },
  maintenance: { label: "Manutenção", icon: Wrench, cls: "bg-blue-500/20 text-blue-500" },
  trip: { label: "Viagem", icon: Route, cls: "bg-slate-500/20 text-slate-500" },
};

export default function FleetPage() {
  const { token } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [logs, setLogs] = useState([]);
  const [vehicleModal, setVehicleModal] = useState(false);
  const [logModal, setLogModal] = useState(false);
  const [vForm, setVForm] = useState({ plate: "", brand: "", model: "", year: "" });
  const [lForm, setLForm] = useState({ vehicle_id: "", type: "fuel", cost: "", odometer: "", description: "" });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const [v, l] = await Promise.all([
        axios.get(`${API_URL}/fleet/vehicles`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/fleet/logs`, { headers, withCredentials: true }),
      ]);
      setVehicles(v.data); setLogs(l.data);
    } catch { toast.error("Erro ao carregar frota"); }
  };

  const createVehicle = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/fleet/vehicles`, { ...vForm, year: vForm.year ? parseInt(vForm.year) : null }, { headers, withCredentials: true });
      toast.success("Veículo registado");
      setVehicleModal(false); setVForm({ plate: "", brand: "", model: "", year: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const deleteVehicle = async (v) => {
    if (!window.confirm(`Remover veículo "${v.plate}"?`)) return;
    await axios.delete(`${API_URL}/fleet/vehicles/${v.vehicle_id}`, { headers, withCredentials: true });
    fetchAll();
  };

  const createLog = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/fleet/logs`, {
        ...lForm,
        cost: toBaseCurrency(lForm.cost),
        odometer: lForm.odometer ? parseFloat(lForm.odometer) : null,
      }, { headers, withCredentials: true });
      toast.success("Registo criado" + (parseFloat(lForm.cost) > 0 && ["fuel", "maintenance"].includes(lForm.type) ? " (despesa lançada no Financeiro)" : ""));
      setLogModal(false); setLForm({ vehicle_id: "", type: "fuel", cost: "", odometer: "", description: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const totalCosts = logs.reduce((s, l) => s + (l.cost || 0), 0);

  return (
    <div className="space-y-6" data-testid="fleet-page">
      <div>
        <h1 className="text-3xl font-bold font-['Outfit']">Frota</h1>
        <p className="text-muted-foreground">Veículos, combustível, manutenções e custos</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Veículos</p>
          <p className="text-2xl font-bold">{vehicles.length}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Custos Totais da Frota</p>
          <p className="text-2xl font-bold font-mono">{formatCurrency(totalCosts)}</p>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="vehicles">
        <TabsList>
          <TabsTrigger value="vehicles" data-testid="fleet-tab-vehicles">Veículos</TabsTrigger>
          <TabsTrigger value="logs" data-testid="fleet-tab-logs">Registos</TabsTrigger>
        </TabsList>

        <TabsContent value="vehicles" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button onClick={() => setVehicleModal(true)} data-testid="add-vehicle-btn"><Plus className="w-4 h-4 mr-2" /> Novo Veículo</Button>
          </div>
          {vehicles.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground"><Car className="w-10 h-10 mx-auto mb-3 opacity-40" />Nenhum veículo registado</CardContent></Card>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {vehicles.map((v) => (
                <Card key={v.vehicle_id} data-testid={`vehicle-${v.vehicle_id}`}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-mono font-bold text-lg">{v.plate}</p>
                        <p className="text-sm text-muted-foreground">{[v.brand, v.model, v.year].filter(Boolean).join(" · ")}</p>
                      </div>
                      <Car className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex justify-between items-center">
                      <p className="text-xs text-muted-foreground font-mono">
                        Custos: {formatCurrency(logs.filter((l) => l.vehicle_id === v.vehicle_id).reduce((s, l) => s + (l.cost || 0), 0))}
                      </p>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteVehicle(v)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="logs" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button onClick={() => setLogModal(true)} disabled={vehicles.length === 0} data-testid="add-fleet-log-btn"><Plus className="w-4 h-4 mr-2" /> Novo Registo</Button>
          </div>
          <div className="space-y-2">
            {logs.map((l) => {
              const t = LOG_TYPES[l.type] || LOG_TYPES.trip;
              return (
                <Card key={l.log_id}>
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge className={t.cls}>{t.label}</Badge>
                      <div>
                        <p className="font-medium text-sm">{l.vehicle_plate} {l.description ? `— ${l.description}` : ""}</p>
                        <p className="text-xs text-muted-foreground">{l.odometer ? `${l.odometer} km · ` : ""}{formatDate(l.created_at)}</p>
                      </div>
                    </div>
                    <p className="font-mono font-bold">{l.cost ? formatCurrency(l.cost) : "-"}</p>
                  </CardContent>
                </Card>
              );
            })}
            {logs.length === 0 && <Card><CardContent className="py-10 text-center text-muted-foreground">Nenhum registo</CardContent></Card>}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={vehicleModal} onOpenChange={setVehicleModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Novo Veículo</DialogTitle></DialogHeader>
          <form onSubmit={createVehicle} className="space-y-3">
            <div className="space-y-1"><Label>Matrícula *</Label><Input required value={vForm.plate} onChange={(e) => setVForm({ ...vForm, plate: e.target.value })} placeholder="LD-00-00-AA" data-testid="vehicle-plate-input" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label>Marca</Label><Input value={vForm.brand} onChange={(e) => setVForm({ ...vForm, brand: e.target.value })} /></div>
              <div className="space-y-1"><Label>Modelo</Label><Input value={vForm.model} onChange={(e) => setVForm({ ...vForm, model: e.target.value })} /></div>
            </div>
            <div className="space-y-1"><Label>Ano</Label><Input type="number" value={vForm.year} onChange={(e) => setVForm({ ...vForm, year: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setVehicleModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-vehicle-btn">Registar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={logModal} onOpenChange={setLogModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Novo Registo de Frota</DialogTitle></DialogHeader>
          <form onSubmit={createLog} className="space-y-3">
            <div className="space-y-1">
              <Label>Veículo *</Label>
              <Select value={lForm.vehicle_id} onValueChange={(v) => setLForm({ ...lForm, vehicle_id: v })}>
                <SelectTrigger data-testid="log-vehicle-select"><SelectValue placeholder="Escolher" /></SelectTrigger>
                <SelectContent>{vehicles.map((v) => <SelectItem key={v.vehicle_id} value={v.vehicle_id}>{v.plate}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Tipo</Label>
              <Select value={lForm.type} onValueChange={(v) => setLForm({ ...lForm, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fuel">Combustível</SelectItem>
                  <SelectItem value="maintenance">Manutenção</SelectItem>
                  <SelectItem value="trip">Viagem</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label>Custo</Label><Input type="number" step="0.01" min="0" value={lForm.cost} onChange={(e) => setLForm({ ...lForm, cost: e.target.value })} data-testid="log-cost-input" /></div>
              <div className="space-y-1"><Label>Odómetro (km)</Label><Input type="number" min="0" value={lForm.odometer} onChange={(e) => setLForm({ ...lForm, odometer: e.target.value })} /></div>
            </div>
            <div className="space-y-1"><Label>Descrição</Label><Input value={lForm.description} onChange={(e) => setLForm({ ...lForm, description: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setLogModal(false)}>Cancelar</Button>
              <Button type="submit" disabled={!lForm.vehicle_id} data-testid="save-fleet-log-btn">Registar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
